/* eslint-disable camelcase */
import { invisiblyApi } from '../api/invisibly-api';
import { APITagType } from '../api/invisibly-api.types';
import type {
  CreateAnswerClusterRequest,
  CreateAnswerMappingRequest,
  CreateAnswerMappingResponseItem,
  CreateQuestionClusterRequest,
  CreateQuestionMappingRequest,
  CreateQuestionMappingResponseItem,
  CreateSurveyGroupRequest,
  DeleteSurveyGroupResponseItem,
  PatchSurveyGroupRequest,
  SurveyGroupApiDetail,
  SurveyGroupPreview,
  ShareSurveyGroupResultsResponse,
  SurveyGroupResultsPayload,
  type SurveyGroupResultsQueryParams,
} from 'src/types/survey-group-api.types';
import { RootState } from '../redux-store';
import ReduxService from 'src/redux/redux.service';

// Survey groups are b2b-only. Do not rewrite these URLs to `client/…`:
// `GET client/survey-groups` collides with ClientViewSet retrieve.

const getClientId = () => {
  const state = ReduxService.Store.getState() as RootState;
  const isClientActive = state.userState.activeClient;
  return isClientActive ? state.userState.activeClient?.ext_client_id ?? null : null;
};

function cleanResultsQueryParams(
  params: SurveyGroupResultsQueryParams | undefined
): Record<string, string | number> | undefined {
  if (!params) {
    return undefined;
  }
  const out: Record<string, string | number> = {};
  if (params.include !== undefined && params.include !== '') {
    out.include = params.include;
  }
  if (params.selected_survey_answers !== undefined && params.selected_survey_answers !== '') {
    out.selected_survey_answers = params.selected_survey_answers;
  }
  if (params.start_date !== undefined && params.start_date !== '') {
    out.start_date = params.start_date;
  }
  if (params.end_date !== undefined && params.end_date !== '') {
    out.end_date = params.end_date;
  }
  if (params.interval !== undefined) {
    out.interval = params.interval;
  }
  return Object.keys(out).length ? out : undefined;
}

function unwrapItem<T>(response: { item: T }): T {
  return response.item;
}

function normalizeListResponse(response: { item: unknown }): SurveyGroupApiDetail[] {
  const { item } = response;
  if (Array.isArray(item)) {
    return item as SurveyGroupApiDetail[];
  }
  if (item && typeof item === 'object') {
    const r = item as { results?: unknown[]; items?: unknown[] };
    if (Array.isArray(r.results)) {
      return r.results as SurveyGroupApiDetail[];
    }
    if (Array.isArray(r.items)) {
      return r.items as SurveyGroupApiDetail[];
    }
  }
  return [];
}

const surveyGroupsEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getSurveyGroups: builder.query<SurveyGroupApiDetail[], { page?: number; page_size?: number } | void>({
      query: (params) => ({
        method: 'get',
        url: 'b2b/survey-groups',
        params: params ?? undefined,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: unknown }) => normalizeListResponse(response),
      providesTags: [{ id: 'LIST', type: APITagType.SurveyGroups }],
    }),

    getSurveyGroup: builder.query<SurveyGroupApiDetail, string>({
      query: (groupId) => ({
        method: 'get',
        url: (`b2b/survey-groups/${groupId}`),
      }),
      transformResponse: unwrapItem<SurveyGroupApiDetail>,
      providesTags: (_result, _err, groupId) => [{ id: groupId, type: APITagType.SurveyGroups }],
    }),

    getSurveyGroupPreview: builder.query<SurveyGroupPreview, string>({
      query: (groupId) => ({
        method: 'get',
        url: (`b2b/survey-groups/${groupId}/preview`),
      }),
      transformResponse: unwrapItem<SurveyGroupPreview>,
      providesTags: (_result, _err, groupId) => [{ id: `${groupId}-preview`, type: APITagType.SurveyGroups }],
    }),

    createSurveyGroup: builder.mutation<SurveyGroupApiDetail, CreateSurveyGroupRequest>({
      query: (body) => ({
        method: 'post',
        url: ('b2b/survey-groups'),
        body,
      }),
      transformResponse: unwrapItem<SurveyGroupApiDetail>,
      invalidatesTags: [{ id: 'LIST', type: APITagType.SurveyGroups }],
    }),

    updateSurveyGroup: builder.mutation<SurveyGroupApiDetail, { groupId: string; body: PatchSurveyGroupRequest }>({
      query: ({ groupId, body }) => ({
        method: 'patch',
        url: (`b2b/survey-groups/${groupId}`),
        body,
      }),
      transformResponse: unwrapItem<SurveyGroupApiDetail>,
      invalidatesTags: (_result, _err, { groupId }) => [
        { id: 'LIST', type: APITagType.SurveyGroups },
        { id: groupId, type: APITagType.SurveyGroups },
      ],
    }),

    deleteSurveyGroup: builder.mutation<DeleteSurveyGroupResponseItem, string>({
      query: (groupId) => ({
        method: 'delete',
        url: (`b2b/survey-groups/${groupId}`),
      }),
      transformResponse: unwrapItem<DeleteSurveyGroupResponseItem>,
      invalidatesTags: (_result, _err, groupId) => [
        { id: 'LIST', type: APITagType.SurveyGroups },
        { id: groupId, type: APITagType.SurveyGroups },
      ],
    }),

    createQuestionCluster: builder.mutation<
    { id: string },
    { groupId: string; body: CreateQuestionClusterRequest }
    >({
      query: ({ groupId, body }) => ({
        method: 'post',
        url: (`b2b/survey-groups/${groupId}/question-clusters`),
        body,
      }),
      transformResponse: unwrapItem<{ id: string }>,
    }),

    createQuestionMapping: builder.mutation<
    CreateQuestionMappingResponseItem,
    { groupId: string; body: CreateQuestionMappingRequest }
    >({
      query: ({ groupId, body }) => ({
        method: 'post',
        url: (`b2b/survey-groups/${groupId}/question-mappings`),
        body,
      }),
      transformResponse: unwrapItem<CreateQuestionMappingResponseItem>,
    }),

    createAnswerCluster: builder.mutation<
    { id: string },
    { groupId: string; body: CreateAnswerClusterRequest }
    >({
      query: ({ groupId, body }) => ({
        method: 'post',
        url: (`b2b/survey-groups/${groupId}/answer-clusters`),
        body,
      }),
      transformResponse: unwrapItem<{ id: string }>,
    }),

    createAnswerMapping: builder.mutation<
    CreateAnswerMappingResponseItem,
    { groupId: string; body: CreateAnswerMappingRequest }
    >({
      query: ({ groupId, body }) => ({
        method: 'post',
        url: (`b2b/survey-groups/${groupId}/answer-mappings`),
        body,
      }),
      transformResponse: unwrapItem<CreateAnswerMappingResponseItem>,
    }),

    shareSurveyGroupResults: builder.mutation<ShareSurveyGroupResultsResponse, string>({
      query: (groupId) => ({
        method: 'post',
        url: (`b2b/survey-groups/${groupId}/share-results`),
        params: {
          client_id: getClientId(),
        },
      }),
      transformResponse: unwrapItem<ShareSurveyGroupResultsResponse>,
      invalidatesTags: (_result, _err, groupId) => [{ id: groupId, type: APITagType.SurveyGroups }],
    }),

    getSurveyGroupResults: builder.query<
    SurveyGroupResultsPayload,
    { groupId: string } & SurveyGroupResultsQueryParams
    >({
      query: ({ groupId, ...queryParams }) => ({
        method: 'get',
        url: (`b2b/survey/survey-group/${groupId}/results`),
        params: cleanResultsQueryParams(queryParams),
      }),
      transformResponse: unwrapItem<SurveyGroupResultsPayload>,
      providesTags: (_result, _err, { groupId }) => [
        { id: `${groupId}-results`, type: APITagType.SurveyGroups },
      ],
    }),

    getSurveyGroupSharedResults: builder.query<
    SurveyGroupResultsPayload,
    { shareId: string } & SurveyGroupResultsQueryParams
    >({
      query: ({ shareId, ...queryParams }) => ({
        method: 'get',
        url: (`b2b/survey/survey-group/shared/${shareId}/results`),
        params: cleanResultsQueryParams(queryParams),
      }),
      transformResponse: unwrapItem<SurveyGroupResultsPayload>,
      providesTags: (_result, _err, { shareId }) => [
        { id: `shared-${shareId}-results`, type: APITagType.SurveyGroups },
      ],
    }),
  }),
});

export const {
  useGetSurveyGroupsQuery,
  useGetSurveyGroupQuery,
  useGetSurveyGroupPreviewQuery,
  useLazyGetSurveyGroupPreviewQuery,
  useCreateSurveyGroupMutation,
  useUpdateSurveyGroupMutation,
  useDeleteSurveyGroupMutation,
  useCreateQuestionClusterMutation,
  useCreateQuestionMappingMutation,
  useCreateAnswerClusterMutation,
  useCreateAnswerMappingMutation,
  useShareSurveyGroupResultsMutation,
  useGetSurveyGroupResultsQuery,
  useLazyGetSurveyGroupResultsQuery,
  useGetSurveyGroupSharedResultsQuery,
  useLazyGetSurveyGroupSharedResultsQuery,
} = surveyGroupsEndpoints;

export { surveyGroupsEndpoints };
